import { useEffect, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { useExamples } from "../../hooks/useExamples";
import { formatJSON } from "../../lib/format";
import { JSONEditor } from "../JSONEditor";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { ValidateResponse } from "../../types/routecore";

// Validação do payload sem consumir cota. Útil para CI ou para clientes
// que querem checar antes de enfileirar.
export function TabValidate() {
  const { examples } = useExamples();
  const { lastPayload, setLastPayload } = useAppState();
  const [payload, setPayload] = useState<string>(lastPayload ?? "");
  const [result, setResult] = useState<APIResult<ValidateResponse> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (payload) return;
    const target = examples.find((e) => e.name === "invalid-request") ?? examples[0];
    if (!target) return;
    api.getExample(target.name).then((r) => {
      const text = r.ok ? formatJSON(r.data ?? r.raw) : r.raw;
      setPayload(text);
      setLastPayload(text);
    });
  }, [payload, examples, setLastPayload]);

  async function run() {
    if (!payload.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch (err) {
      setResult({
        ok: false,
        status: 0,
        data: null,
        raw: String(err),
        latencyMs: 0,
      });
      return;
    }
    setLoading(true);
    setResult(null);
    const r = await api.validate(parsed);
    setResult(r as APIResult<ValidateResponse>);
    setLoading(false);
    setLastPayload(payload);
  }

  // Só é uma resposta de validação quando o upstream devolveu 200 com o campo
  // `valid` (bool). Respostas de erro (400/5xx — ex.: o exemplo invalid-request,
  // que o decoder rejeita com 400) caem só no ResponseCard, sem um card "Resumo"
  // enganoso mostrando "Válido: não / Erros: 0" para um corpo que não é um
  // ValidateResponse.
  const summary =
    result?.ok && typeof result.data?.valid === "boolean" ? result.data : undefined;
  const errors = summary?.errors ?? [];
  const warnings = summary?.warnings ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Validar payload"
        subtitle="Devolve `valid: true|false` + lista de erros/warnings. Não persiste nada, não publica evento."
        endpoint={{ method: "POST", path: "/v1/route-optimization/validate" }}
        actions={
          <button type="button" className="ks-btn-primary" disabled={loading} onClick={run}>
            {loading ? "validando..." : "Validar"}
          </button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <SectionCard
          title="Payload"
          description="Estrutura idêntica ao Criar Job. Sem regras de negócio aplicadas (apenas validador)."
        >
          <JSONEditor value={payload} onChange={setPayload} rows={22} />
        </SectionCard>

        <div className="space-y-4">
          {summary && (
            <SectionCard
              title="Resumo"
              description={summary.valid ? "Payload aceito sem erros." : "Payload com erros."}
            >
              <div className="grid gap-2 sm:grid-cols-3">
                <Stat label="Válido" value={summary.valid ? "sim" : "não"} tone={summary.valid ? "ok" : "err"} />
                <Stat label="Erros" value={errors.length.toString()} tone={errors.length ? "err" : "ok"} />
                <Stat label="Avisos" value={warnings.length.toString()} tone={warnings.length ? "warn" : "ok"} />
              </div>
              {errors.length > 0 && (
                <div className="mt-4">
                  <p className="ks-label">Erros</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {errors.map((e, i) => (
                      <li key={i} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                        <p className="text-xs font-mono text-rose-700">{e.field}</p>
                        <p className="text-rose-900">{e.message}</p>
                        <p className="text-[10px] uppercase tracking-wide text-rose-700">
                          {e.code}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {warnings.length > 0 && (
                <div className="mt-4">
                  <p className="ks-label">Avisos</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {warnings.map((w, i) => (
                      <li key={i} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-xs font-mono text-amber-800">{w.field}</p>
                        <p className="text-amber-900">{w.message}</p>
                        <p className="text-[10px] uppercase tracking-wide text-amber-700">
                          {w.code}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </SectionCard>
          )}

          <ResponseCard
            result={result}
            loading={loading}
            emptyHint="A resposta da API aparecerá aqui (status 200 mesmo com valid=false)."
          />
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "err";
}) {
  const cls =
    tone === "ok"
      ? "bg-brand-50 text-brand-800"
      : tone === "warn"
        ? "bg-amber-50 text-amber-800"
        : "bg-rose-50 text-rose-800";
  return (
    <div className={`rounded-lg border border-ink-200 px-3 py-2 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
